/* ==========================================================================
   Royal Honey — sections
   Announcement rotator · video · split hero · shop-the-look hotspots ·
   theme editor hooks
   ========================================================================== */
(() => {
  'use strict';

  const N = window.SR || { settings: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ---------- Announcement: one message at a time ---------- */
  define('text-rotator', class extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-rotate-item]')];
      if (this.items.length < 2 || reduceMotion.matches) return;
      this.index = 0;
      const ms = Math.max(parseInt(this.dataset.interval, 10) || 5000, 2500);
      const start = () => { clearInterval(this.timer); this.timer = setInterval(() => this.next(), ms); };
      start();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', () => clearInterval(this.timer));
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  });

  /* ---------- Video with play / pause ---------- */
  define('video-player', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-video-toggle]');
      if (!this.video) return;
      if (reduceMotion.matches) { this.video.removeAttribute('autoplay'); this.video.pause(); }
      this.toggle?.addEventListener('click', () => { if (this.video.paused) this.video.play(); else this.video.pause(); });
      ['play', 'pause'].forEach((ev) => this.video.addEventListener(ev, () => this.sync()));
      this.sync();
    }
    sync() {
      const paused = this.video.paused;
      this.classList.toggle('is-paused', paused);
      this.toggle?.setAttribute('aria-label', paused ? N.strings.play : N.strings.pause);
    }
  });

  /* ---------- Deferred video (loads the player on demand) ---------- */
  define('deferred-video', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-video-play]')?.addEventListener('click', () => this.load());
    }
    load() {
      const tpl = this.querySelector('template');
      if (!tpl || this.classList.contains('is-playing')) return;
      const node = tpl.content.firstElementChild?.cloneNode(true);
      if (!node) return;
      this.appendChild(node);
      this.classList.add('is-playing');
      if (node.tagName === 'VIDEO') node.play().catch(() => {});
    }
  });

  /* ---------- Split hero: the hovered or focused universe widens ---------- */
  define('split-hero', class extends HTMLElement {
    connectedCallback() {
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.panels.forEach((panel) => {
        panel.addEventListener('pointerenter', () => this.focusPanel(panel));
        panel.addEventListener('focusin', () => this.focusPanel(panel));
      });
      this.addEventListener('pointerleave', () => this.focusPanel(null));
      // Keep the centre tagline on the seam while a panel widens.
      const first = this.panels[0];
      if (first && this.panels.length === 2 && 'ResizeObserver' in window) {
        this.seam = new ResizeObserver(() => this.style.setProperty('--seam', `${first.offsetWidth}px`));
        this.seam.observe(first);
      }
      // A soft light follows the pointer and the illustration leans towards it.
      if (document.body.classList.contains('has-hover-fx') && window.matchMedia('(hover: hover) and (pointer: fine)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panels.forEach((panel) => {
          let frame;
          panel.addEventListener('pointermove', (e) => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              const r = panel.getBoundingClientRect();
              const x = (e.clientX - r.left) / r.width;
              const y = (e.clientY - r.top) / r.height;
              panel.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
              panel.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
              panel.style.setProperty('--tx', (x - 0.5).toFixed(3));
              panel.style.setProperty('--ty', (y - 0.5).toFixed(3));
            });
          });
          panel.addEventListener('pointerleave', () => {
            cancelAnimationFrame(frame);
            panel.style.setProperty('--tx', '0');
            panel.style.setProperty('--ty', '0');
          });
        });
      }
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }
    disconnectedCallback() { this.seam?.disconnect(); }
    focusPanel(panel) {
      this.panels.forEach((p) => p.classList.toggle('is-focus', p === panel));
      this.classList.toggle('has-focus', Boolean(panel));
    }
  });

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const panel = block.closest('[role="tabpanel"]');
    if (panel) panel.closest('tab-panels')?.selectPanel(panel);
    const tab = block.matches?.('[role="tab"]') ? block : block.querySelector?.('[role="tab"]');
    if (tab) tab.click();
    const heroPanel = block.closest('[data-panel]');
    if (heroPanel) heroPanel.closest('split-hero')?.focusPanel(heroPanel);
    const opener = block.querySelector?.('[data-dialog-open]');
    if (opener && N.openDialog) N.openDialog(document.getElementById(opener.dataset.dialogOpen), opener);
    const rotItem = block.closest('[data-rotate-item]');
    if (rotItem) { const r = rotItem.closest('text-rotator'); r?.items?.forEach((i) => i.classList.toggle('is-active', i === rotItem)); clearInterval(r?.timer); }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    e.target.querySelectorAll?.('dialog[open]').forEach((d) => d.close());
    document.querySelectorAll('dialog.modal[open]').forEach((d) => d.close());
    e.target.closest?.('split-hero')?.focusPanel(null);
  });
})();
